import { Box, Heading, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, MultiSelectBuild, SelectBuild } from 'components/molecules'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import jwt_decode from 'jwt-decode'
import { AuthTokens } from 'models'
import { BottonLine, FormContainer } from 'pages/Report/styles'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { getShippingFile } from 'services/report'
import { generateFile } from 'utils/blob'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { TxtGenerator } from './components/TxtGenerator'
import { FormReportValue } from './form'
import { schema } from './schema'
import { FormShippingFile } from './types'

const getMesAnterior = (): string => {
    const hoje = new Date()
    hoje.setMonth(hoje.getMonth() - 1)

    const year = hoje.getFullYear()
    const month = String(hoje.getMonth() + 1).padStart(2, '0')
    return `${year}-${month}`
}

export const ShippingFile: React.FC = () => {
    const {
        register,
        formState: { errors },
        watch,
        control
    } = useForm<FormShippingFile>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            periodo: getMesAnterior()
        }
    })

    const { isBackoffice, currentUser } = useAuthContext()
    const { currentInstitution, currentAgent } = useInstitutionsContext()

    const [loadingReport, setLoadingReport] = useState<boolean>(false)

    const toast = useToast()

    const decodedIdToken: AuthTokens = jwt_decode(
        currentUser?.idToken as string
    )

    const all = watch()

    const callAPI = (type: 'SERVICO' | 'TAXA') => {
        if (!all?.vencimento) {
            toast({
                title: 'Data de vencimento obrigatória',
                description:
                    'Por favor, preencha a data de vencimento para continuar.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            return
        }

        // Separar ano e mês do campo "periodo"
        const [ano, mes] = (all?.periodo ?? '').split('-')

        const queryRemoveEmptyValues = removeEmptyValues({
            ...all,
            periodoInicial: `${ano}-${mes}-01`,
            periodoFinal: `${ano}-${mes}-${new Date(+ano, +mes, 0).getDate()}`,
            vencimento: formatter.formatDateToPayload(all?.vencimento ?? ''),
            cnpjAgente: currentAgent?.cnpj ?? '',
            instituicaoFinanceira: currentInstitution?.id ?? ''
        })

        const query = {
            ...queryRemoveEmptyValues,
            tipo: type
        }

        downloadFiles(query)
    }

    const downloadFiles = async (query: any) => {
        setLoadingReport(true)
        try {
            const res = await getShippingFile(query)

            generateFile('TXT', res)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo TXT foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingReport(false)
        }
    }

    const form = useMemo(() => FormReportValue(decodedIdToken?.UFDetran), [])

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingBottom="24px"
            >
                <Heading size="md">Arquivo Remessa</Heading>
                <TxtGenerator callAPI={callAPI} loading={loadingReport} />
            </Box>
            <form>
                <Box
                    paddingTop="24px"
                    bgColor="white"
                    borderRadius={6}
                    h="100%"
                >
                    <Layout type="container" px={8} full display="grid" gap={8}>
                        <FormContainer
                            minH={{
                                base: 'calc(100vh - 310px)',
                                md: 'calc(100vh - 260px)'
                            }}
                        >
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {form.map(
                                    (
                                        { layout, input, select, multiSelect },
                                        key
                                    ) => (
                                        <Layout {...layout} key={key}>
                                            {multiSelect ? (
                                                <Controller
                                                    name={multiSelect.id}
                                                    control={control}
                                                    defaultValue={
                                                        multiSelect.defaultValue ||
                                                        ''
                                                    }
                                                    render={({ field }) => (
                                                        <MultiSelectBuild
                                                            {...multiSelect}
                                                            value={
                                                                field.value ||
                                                                ''
                                                            }
                                                            onChange={
                                                                field.onChange
                                                            }
                                                            onBlur={
                                                                field.onBlur
                                                            }
                                                            err={
                                                                errors[
                                                                    multiSelect
                                                                        .id
                                                                ] as any
                                                            }
                                                        />
                                                    )}
                                                />
                                            ) : select ? (
                                                <SelectBuild
                                                    {...select}
                                                    err={errors[select.id]}
                                                    {...register(select.id)}
                                                />
                                            ) : (
                                                input && (
                                                    <InputBuild
                                                        {...input}
                                                        err={errors[input.id]}
                                                        {...register(input.id)}
                                                        value={watch(input.id)}
                                                    />
                                                )
                                            )}
                                        </Layout>
                                    )
                                )}
                                <Box>
                                    <BottonLine />
                                </Box>
                            </Layout>
                        </FormContainer>
                    </Layout>
                </Box>
            </form>
        </Box>
    )
}
